import { useState } from 'react';
import { Spinner, money } from '@busbook/ui';
import { Page } from '../components/Shell.js';
import { useResource } from '../lib/useResource.js';
import { useT } from '../i18n/index.js';

/**
 * This operator's own route performance.
 *
 * Scoped by the SERVER to the signed-in staff account's tenant — never by a
 * parameter the browser could change. Same aggregate the platform console reads,
 * with one difference that matters to an operator: the last column is what they
 * KEEP, gross less the platform's commission, because that is the number they run
 * the business on.
 */

interface RouteRow {
  routeId: string;
  routeName: string;
  origin: string | null;
  destination: string | null;
  currency: string;
  departures: number;
  seatsOffered: number;
  seatsSold: number;
  loadFactor: number;
  bookings: number;
  gmvCents: number;
  commissionCents: number;
}

const DAYS = 30;

/**
 * A load factor is a proportion, and a bar reads as one at a glance where a
 * percentage does not. The fill is amber only once a route is carrying more than
 * half its seats — below that the number is the story, not the colour.
 */
function LoadBar({ value }: { value: number }) {
  const pct = Math.round(value * 100);
  const tone = value >= 0.5 ? 'var(--signal)' : value >= 0.2 ? 'var(--hold)' : 'var(--chalk-faint)';
  return (
    <span className="flex items-center justify-end gap-2">
      <span
        className="hidden h-1.5 w-16 overflow-hidden rounded-full sm:block"
        style={{ background: 'var(--ink-700)' }}
        aria-hidden
      >
        <span className="block h-full rounded-full" style={{ width: `${pct}%`, background: tone }} />
      </span>
      <span className="tabular w-10 text-right font-medium" style={{ color: tone }}>
        {pct}%
      </span>
    </span>
  );
}

export function Routes() {
  const t = useT();
  const [window, setWindow] = useState<'departed' | 'selling'>('departed');
  const resource = useResource<{ days: number; routes: RouteRow[] }>(
    `/admin/route-performance?days=${DAYS}&window=${window}`,
  );

  const toggle = (
    <div
      className="flex items-center rounded-lg border p-0.5"
      style={{ borderColor: 'var(--hairline-strong)' }}
      role="tablist"
    >
      {(['departed', 'selling'] as const).map((mode) => (
        <button
          key={mode}
          type="button"
          role="tab"
          aria-selected={window === mode}
          onClick={() => setWindow(mode)}
          className="rounded-[6px] px-2.5 py-1 text-[0.75rem] font-medium transition-colors"
          style={{
            background: window === mode ? 'var(--signal-dim)' : 'transparent',
            color: window === mode ? 'var(--signal)' : 'var(--chalk-dim)',
          }}
        >
          {t(mode === 'departed' ? 'routes.departed' : 'routes.selling')}
        </button>
      ))}
    </div>
  );

  const rows = resource.data?.routes ?? [];

  return (
    <Page
      title={t('routes.title')}
      description={t(
        window === 'departed' ? 'routes.windowHint.departed' : 'routes.windowHint.selling',
        { days: DAYS },
      )}
      actions={toggle}
    >
      {resource.loading ? (
        <div className="grid place-items-center py-20">
          <Spinner label={t('common.loading')} />
        </div>
      ) : rows.length === 0 ? (
        <div
          className="rounded-xl border px-5 py-12 text-center"
          style={{ background: 'var(--ink-850)', borderColor: 'var(--hairline)' }}
        >
          <p className="text-[0.875rem] font-medium">{t('routes.emptyTitle')}</p>
          <p className="mt-1 text-[0.8125rem]" style={{ color: 'var(--chalk-faint)' }}>
            {t('routes.emptyBody')}
          </p>
        </div>
      ) : (
        <div
          className="overflow-x-auto rounded-xl border"
          style={{ background: 'var(--ink-850)', borderColor: 'var(--hairline)' }}
        >
          <table className="w-full border-collapse text-[0.8125rem]">
            <thead>
              <tr>
                {[
                  { k: 'routes.colRoute', numeric: false },
                  { k: 'routes.colDepartures', numeric: true },
                  { k: 'routes.colSeats', numeric: true },
                  { k: 'routes.colLoad', numeric: true },
                  { k: 'routes.colGross', numeric: true },
                  { k: 'routes.colNet', numeric: true },
                ].map((col) => (
                  <th
                    key={col.k}
                    scope="col"
                    className={`border-b px-4 py-2.5 text-[0.625rem] font-semibold uppercase tracking-[0.16em] ${
                      col.numeric ? 'text-right' : 'text-left'
                    }`}
                    style={{ borderColor: 'var(--hairline)', color: 'var(--chalk-faint)' }}
                  >
                    {t(col.k as Parameters<typeof t>[0])}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.routeId} className="transition-colors hover:bg-[var(--ink-800)]">
                  <td className="border-b px-4 py-3" style={{ borderColor: 'var(--hairline)' }}>
                    {/* Where it goes, not what it was named — a board reads as O → D. */}
                    <span className="font-medium">
                      {row.origin ?? row.routeName}
                      <span style={{ color: 'var(--chalk-faint)' }}> → </span>
                      {row.destination ?? ''}
                    </span>
                    <span className="mt-0.5 block text-[0.6875rem]" style={{ color: 'var(--chalk-faint)' }}>
                      {row.routeName}
                    </span>
                  </td>
                  <td className="border-b px-4 py-3 text-right tabular" style={{ borderColor: 'var(--hairline)' }}>
                    {row.departures}
                  </td>
                  <td className="border-b px-4 py-3 text-right tabular" style={{ borderColor: 'var(--hairline)' }}>
                    <span>{row.seatsSold}</span>
                    <span style={{ color: 'var(--chalk-faint)' }}> / {row.seatsOffered}</span>
                  </td>
                  <td className="border-b px-4 py-3 text-right" style={{ borderColor: 'var(--hairline)' }}>
                    <LoadBar value={row.loadFactor} />
                  </td>
                  <td className="border-b px-4 py-3 text-right tabular" style={{ borderColor: 'var(--hairline)' }}>
                    {money(row.gmvCents, row.currency)}
                  </td>
                  <td
                    className="border-b px-4 py-3 text-right font-medium tabular"
                    style={{ borderColor: 'var(--hairline)', color: 'var(--signal)' }}
                  >
                    {/* What the operator keeps: gross less the platform's cut. */}
                    {money(row.gmvCents - row.commissionCents, row.currency)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Page>
  );
}
